import { apiRequest } from './client';
import type {
  BackupFile,
  Branch,
  BranchSubscription,
  Category,
  Company,
  CostCalculation,
  DashboardData,
  InventoryMovement,
  Paginated,
  Payment,
  PlatformDashboardData,
  Quote,
  Recipe,
  RecipeComponent,
  RecipeCostResult,
  Supply,
  Unit,
  User,
  Sale,
  QuoteFormat,
} from './types';

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

// --- Auth ---
export const AuthApi = {
  login: (username: string, password: string) => apiRequest<{ user: User }>('/api/auth/login', { method: 'POST', body: { username, password } }),
  logout: () => apiRequest<void>('/api/auth/logout', { method: 'POST' }),
  me: () => apiRequest<{ user: User }>('/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest<void>('/api/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } }),
};

// --- Users (admin) ---
export const UsersApi = {
  quota: () => apiRequest<{ maxUsers: number; activeUsers: number }>('/api/users/quota'),
  list: (params: { search?: string; role?: string; isActive?: boolean } = {}) =>
    apiRequest<{ users: User[] }>(`/api/users${qs(params)}`),
  create: (data: { username: string; email: string; fullName: string; role: string; password?: string }) =>
    apiRequest<{ user: User & { temporaryPassword: string } }>('/api/users', { method: 'POST', body: data }),
  update: (id: string, data: Partial<{ email: string; fullName: string; role: string }>) =>
    apiRequest<{ user: User }>(`/api/users/${id}`, { method: 'PUT', body: data }),
  activate: (id: string) => apiRequest<{ user: User }>(`/api/users/${id}/activate`, { method: 'POST' }),
  deactivate: (id: string) => apiRequest<{ user: User }>(`/api/users/${id}/deactivate`, { method: 'POST' }),
  resetPassword: (id: string) => apiRequest<{ temporaryPassword: string }>(`/api/users/${id}/reset-password`, { method: 'POST' }),
  setPassword: (id: string, newPassword: string) => apiRequest<void>(`/api/users/${id}/set-password`, { method: 'POST', body: { newPassword } }),
};

// --- Categories ---
export const CategoriesApi = {
  list: (includeInactive = false) => apiRequest<{ categories: Category[] }>(`/api/categories${qs({ includeInactive })}`),
  create: (data: { code: string; name: string; description?: string }) => apiRequest<{ category: Category }>('/api/categories', { method: 'POST', body: data }),
  update: (id: string, data: Partial<{ name: string; description: string }>) => apiRequest<{ category: Category }>(`/api/categories/${id}`, { method: 'PUT', body: data }),
  activate: (id: string) => apiRequest<{ category: Category }>(`/api/categories/${id}/activate`, { method: 'POST' }),
  deactivate: (id: string) => apiRequest<{ category: Category }>(`/api/categories/${id}/deactivate`, { method: 'POST' }),
};

// --- Units ---
export const UnitsApi = {
  list: (includeInactive = false) => apiRequest<{ units: Unit[] }>(`/api/units${qs({ includeInactive })}`),
  create: (data: { code: string; name: string }) => apiRequest<{ unit: Unit }>('/api/units', { method: 'POST', body: data }),
  update: (id: string, data: Partial<{ name: string }>) => apiRequest<{ unit: Unit }>(`/api/units/${id}`, { method: 'PUT', body: data }),
  activate: (id: string) => apiRequest<{ unit: Unit }>(`/api/units/${id}/activate`, { method: 'POST' }),
  deactivate: (id: string) => apiRequest<{ unit: Unit }>(`/api/units/${id}/deactivate`, { method: 'POST' }),
};

// --- Supplies ---
export const SuppliesApi = {
  list: (params: { search?: string; categoryId?: string; isActive?: boolean; lowStock?: boolean; page?: number; pageSize?: number } = {}) =>
    apiRequest<Paginated<Supply>>(`/api/supplies${qs(params)}`),
  get: (id: string) => apiRequest<{ supply: Supply }>(`/api/supplies/${id}`),
  create: (data: Record<string, unknown>) => apiRequest<{ supply: Supply }>('/api/supplies', { method: 'POST', body: data }),
  update: (id: string, data: Record<string, unknown>) => apiRequest<{ supply: Supply }>(`/api/supplies/${id}`, { method: 'PUT', body: data }),
  activate: (id: string) => apiRequest<{ supply: Supply }>(`/api/supplies/${id}/activate`, { method: 'POST' }),
  deactivate: (id: string) => apiRequest<{ supply: Supply }>(`/api/supplies/${id}/deactivate`, { method: 'POST' }),
};

// --- Inventory ---
export const InventoryApi = {
  movements: (params: { supplyId?: string; type?: string; userId?: string; from?: string; to?: string; page?: number; pageSize?: number } = {}) =>
    apiRequest<Paginated<InventoryMovement>>(`/api/inventory/movements${qs(params)}`),
  register: (data: { supplyId: string; type: string; quantity: number; note?: string }) =>
    apiRequest<{ movement: InventoryMovement }>('/api/inventory/movements', { method: 'POST', body: data }),
  lowStock: () => apiRequest<{ supplies: Supply[] }>('/api/inventory/low-stock'),
};

// --- Recipes ---
export const RecipesApi = {
  list: (params: { search?: string; isActive?: boolean } = {}) => apiRequest<{ recipes: Recipe[] }>(`/api/recipes${qs(params)}`),
  get: (id: string) => apiRequest<{ recipe: Recipe }>(`/api/recipes/${id}`),
  cost: (id: string, quantity = 1) => apiRequest<{ cost: RecipeCostResult; totalProductionCost: string }>(`/api/recipes/${id}/cost${qs({ quantity })}`),
  create: (data: { code: string; name: string; description?: string; laborCost?: number; wastePercentage?: number }) =>
    apiRequest<{ recipe: Recipe }>('/api/recipes', { method: 'POST', body: data }),
  update: (id: string, data: Partial<{ name: string; description: string; laborCost: number; wastePercentage: number }>) =>
    apiRequest<{ recipe: Recipe }>(`/api/recipes/${id}`, { method: 'PUT', body: data }),
  uploadImage: (id: string, file: File) => {
    const body = new FormData();
    body.append('image', file);
    return apiRequest<{ recipe: Recipe }>(`/api/recipes/${id}/image`, { method: 'POST', body, isFormData: true });
  },
  removeImage: (id: string) => apiRequest<void>(`/api/recipes/${id}/image`, { method: 'DELETE' }),
  activate: (id: string) => apiRequest<{ recipe: Recipe }>(`/api/recipes/${id}/activate`, { method: 'POST' }),
  deactivate: (id: string) => apiRequest<{ recipe: Recipe }>(`/api/recipes/${id}/deactivate`, { method: 'POST' }),
  addComponent: (recipeId: string, data: { supplyId?: string; componentRecipeId?: string; quantity: number; unitId: string }) =>
    apiRequest<{ component: RecipeComponent }>(`/api/recipes/${recipeId}/components`, { method: 'POST', body: data }),
  updateComponent: (recipeId: string, componentId: string, data: { quantity?: number; unitId?: string }) =>
    apiRequest<{ component: RecipeComponent }>(`/api/recipes/${recipeId}/components/${componentId}`, { method: 'PUT', body: data }),
  removeComponent: (recipeId: string, componentId: string) =>
    apiRequest<void>(`/api/recipes/${recipeId}/components/${componentId}`, { method: 'DELETE' }),
};

// --- Costs / historial ---
export const CostsApi = {
  calculate: (data: { recipeId: string; quantity: number; consumeInventory: boolean; notes?: string }) =>
    apiRequest<{ calculation: CostCalculation }>('/api/costs/calculate', { method: 'POST', body: data }),
  list: (params: { search?: string; recipeId?: string; userId?: string; from?: string; to?: string; page?: number; pageSize?: number } = {}) =>
    apiRequest<Paginated<CostCalculation>>(`/api/costs${qs(params)}`),
  get: (id: string) => apiRequest<{ calculation: CostCalculation }>(`/api/costs/${id}`),
};

// --- Sales ---
export const SalesApi = {
  create: (data: { clientDocument: string; clientName: string; paymentMethod: string; items: { recipeId: string; quantity: number }[] }) =>
    apiRequest<{ sale: Sale }>('/api/sales', { method: 'POST', body: data }),
  list: (params: { search?: string; page?: number; pageSize?: number; userId?: string; recipeId?: string; from?: string; to?: string } = {}) =>
    apiRequest<Paginated<Sale> & { totalAmount: string }>(`/api/sales${qs(params)}`),
};

// --- Quotes ---
export const QuotesApi = {
  create: (data: {
    clientName: string;
    clientDocument?: string;
    clientAddress?: string;
    clientPhone?: string;
    clientEmail?: string;
    items: { recipeId: string; quantity: number }[];
    notes?: string;
  }) => apiRequest<{ quote: Quote }>('/api/quotes', { method: 'POST', body: data }),
  getFormat: () => apiRequest<{ format: QuoteFormat }>('/api/quotes/format'),
  saveFormat: (data: { logoData?: string | null; footerText?: string | null }) =>
    apiRequest<{ format: QuoteFormat }>('/api/quotes/format', { method: 'PUT', body: data }),
  list: (params: { search?: string; userId?: string; from?: string; to?: string; page?: number; pageSize?: number } = {}) =>
    apiRequest<Paginated<Quote>>(`/api/quotes${qs(params)}`),
  get: (id: string) => apiRequest<{ quote: Quote }>(`/api/quotes/${id}`),
};

// --- Settings ---
export const SettingsApi = {
  getWastePercentage: () => apiRequest<{ wastePercentage: number }>('/api/settings/waste-percentage'),
  setWastePercentage: (wastePercentage: number) => apiRequest<{ wastePercentage: number }>('/api/settings/waste-percentage', { method: 'PUT', body: { wastePercentage } }),
};

export const PlatformApi = {
  dashboard: () => apiRequest<PlatformDashboardData>('/api/platform/dashboard'),
  companies: () => apiRequest<{ companies: Company[] }>('/api/platform/companies'),
  createCompany: (data: Record<string, unknown>) =>
    apiRequest<{ company: Company }>('/api/platform/companies', { method: 'POST', body: data }),
  updateCompany: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ company: Company }>(`/api/platform/companies/${id}`, { method: 'PUT', body: data }),
  setCompanyActive: (id: string, active: boolean) =>
    apiRequest<{ company: Company }>(`/api/platform/companies/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' }),
  branches: () => apiRequest<{ branches: Branch[] }>('/api/platform/branches'),
  branch: (id: string) => apiRequest<{ branch: Branch }>(`/api/platform/branches/${id}`),
  createBranch: (data: Record<string, unknown>) =>
    apiRequest<{ branch: Branch; temporaryPassword: string }>('/api/platform/branches', { method: 'POST', body: data }),
  updateBranch: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ branch: Branch }>(`/api/platform/branches/${id}`, { method: 'PUT', body: data }),
  updateBranchAdmin: (branchId: string, userId: string, data: { username: string; fullName: string; email: string }) =>
    apiRequest<{ user: User }>(`/api/platform/branches/${branchId}/admins/${userId}`, { method: 'PUT', body: data }),
  resetBranchAdminPassword: (branchId: string, userId: string) =>
    apiRequest<{ temporaryPassword: string }>(`/api/platform/branches/${branchId}/admins/${userId}/reset-password`, { method: 'POST' }),
  setBranchUserActive: (branchId: string, userId: string, active: boolean) =>
    apiRequest<{ user: User }>(`/api/platform/branches/${branchId}/users/${userId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' }),
  setBranchActive: (id: string, active: boolean) =>
    apiRequest<{ branch: Branch }>(`/api/platform/branches/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' }),
  setBranchAccess: (id: string, enabled: boolean) =>
    apiRequest<{ branch: Branch }>(`/api/platform/branches/${id}/access`, { method: 'POST', body: { enabled } }),
  renewSubscription: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ subscription: BranchSubscription }>(`/api/platform/branches/${id}/subscriptions`, { method: 'POST', body: data }),
  registerPayment: (branchId: string, subscriptionId: string, data: Record<string, unknown>) =>
    apiRequest<{ payment: Payment }>(`/api/platform/branches/${branchId}/subscriptions/${subscriptionId}/payments`, { method: 'POST', body: data }),
  updatePayment: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ payment: Payment }>(`/api/platform/payments/${id}`, { method: 'PATCH', body: data }),
};

// --- Backups (admin) ---
export const BackupsApi = {
  list: () => apiRequest<{ backups: BackupFile[] }>('/api/backups'),
  create: () => apiRequest<{ backup: BackupFile }>('/api/backups', { method: 'POST' }),
  restore: (fileName: string) => apiRequest<void>('/api/backups/restore', { method: 'POST', body: { fileName, confirm: true } }),
};

// --- Dashboard ---
export const DashboardApi = {
  get: () => apiRequest<DashboardData>('/api/dashboard'),
};
